import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { IButtonBlockData } from '../../types/content-block-data';
import { escapeHTML } from '../../lib/page-builder-lib';
import blocksManager from '../../stores/blocks-manager';
import contentManager from '../../stores/content-manager';
import pageBuilderStore from '../../stores/pageBuilderStore';

/**
 * Converts a ButtonBlock (IContentBlock) to an HTML string.
 * Renders as a <button> with label as its content.
 * 
 * @param block - The IContentBlock (should be blockType "button").
 * @returns HTML string (e.g., <button>Label</button>)
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromButtonBlockToHTML (block: IContentBlock): string {
	if (block.blockType !== "button") return "";
	
	const isEmail = pageBuilderStore.getIsEmail();
	const data = block.blockData as IButtonBlockData;
	const selector = block.selector || "d2d-button";

	let buttonStyle = `color: ${data.color}; `;
	buttonStyle += `background-color: ${data.backgroundColor}; `;
	buttonStyle += `border-color: ${data.borderColor}; `;
	buttonStyle += `border-style: ${data.borderStyle || "solid"}; `;
	buttonStyle += `border-width: ${data.borderWidth?.value ? `${data.borderWidth.value}${data.borderWidth.unit || "px"}` : "0px"}; `;
	buttonStyle += `border-radius: ${blocksManager.dimensionToCss(data.borderRadius) || "8px"}; `;
	buttonStyle += `padding: ${blocksManager.dimensionToCss(data.padding) || "8px"}; `;
	buttonStyle += `margin: ${blocksManager.dimensionToCss(data.margin) || "8px"}; `;
	buttonStyle += `font-family: ${data.fontFamily}; `;
	buttonStyle += `font-size: ${data.fontSize?.value ? `${data.fontSize.value}${data.fontSize.unit || "rem"}` : "1rem"};`;
	buttonStyle += `font-weight: ${data.fontWeight}; `;
	buttonStyle += `line-height: ${data.lineHeight?.value ? `${data.lineHeight.value}${data.lineHeight.unit || ""}` : "normal"}; `;
	buttonStyle += `letter-spacing: ${data.letterSpacing?.value ? `${data.letterSpacing.value}${data.letterSpacing.unit || "px"}` : "normal"};`;
	buttonStyle += `text-decoration: ${data.textDecoration || "none"};`;
	buttonStyle += `width: ${data.width?.value && data.width.value !== "auto"
    ? `${data.width.value}${data.width.unit || "px"}`
    : "auto"};`;
	const hoverStyle = `
		<style>
			.${selector}:hover {
				color: ${data.hoverColor || data.color};
				background-color: ${data.hoverBackgroundColor || data.backgroundColor};
			}
		</style>
	`;

	// --- Link handling
	let href="#";
	if (data.url && data.url !== "#") {
		if (data.actionType === "email") href = `mailto:${data.url}`;
		else if (data.actionType === "phone") href = `tel:${data.url}`;
		else href = data.url;
	}

	// Render as <a> if a valid URL/email/phone is set
	if (data.url && data.url !== "#") {
		const targetAttr = data.target ? ` target="_blank" rel="noopener noreferrer"` : "";
		const openTag = isEmail ? `<tr><td style="text-align: ${data.alignment};">` :  `<div style="text-align: ${data.alignment};">`;
		const btnOpenTag = `<a class="${selector}" href="${href}" style="${buttonStyle}"${targetAttr}>`;
		const btnCloseTag = `</a>`;
		const closeTag =  isEmail ? `</td></tr>` : `</div>`;
		const htmlValue = escapeHTML(data.label ?? "");
		return `${openTag}${btnOpenTag}${htmlValue}${btnCloseTag}${closeTag}`; 
	}


  const openTag = isEmail ? `<tr><td style="text-align: ${data.alignment};">` :  `<div style="text-align: ${data.alignment};">`;
	const btnOpenTag = `<button class="${selector}" style="${buttonStyle}">`;
	const btnCloseTag = `</button>`;
	const closeTag = isEmail ? `</td></tr>` : `</div>`;
	const htmlValue = escapeHTML(data.label ?? "");

	// I am removing the hoverStyle because this can be properly implemented in the next phase of development.
	return `${openTag}${btnOpenTag}${htmlValue}${btnCloseTag}${closeTag}`;
}

/**
 * Serializes a ButtonBlock's data for storage or export.
 * Omits UI-only fields.
 * 
 * @param block - TheIContentBlock (should be blockType "button")
 * @returns A JSON-serializable object.
 */
export function fromButtonBlockToJSON (block: IContentBlock): object {
	if (block.blockType !== "button") return {};

	const data = block.blockData as IButtonBlockData;

	return {
		contentBlockId: block.contentBlockId,
		blockType: block.blockType,
		selector: block.selector,
		blockData: {
			label: data.label,
			actionType: data.actionType,
			url: data.url,
			target: data.target,
		},
	};
}

/**
 * Reconstructs a ButtonBlock from JSON data as an IDesignBlock.
 *
 * @param json - The JSON object for this button block (from storage/export).
 * @returns An IDesignBlock instance, ready for UI/store.
 */
export function fromJSONToButtonBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
	const cssStyles = contentManager.getStylesForSelector(json.selector);

	const newDesignBlockRef = createRef<HTMLDivElement>();
	const newContentBlockRef = createRef<HTMLElement>(); // generic for now

	const block:IDesignBlock = {
		designBlockId: crypto.randomUUID(), // always generate fresh
		designBlockRef: newDesignBlockRef,
		contentBlock: {
			contentBlockId: json.contentBlockId ?? crypto.randomUUID(),
			contentBlockRef: newContentBlockRef,
			selector: json.selector,
			blockType: json.blockType,
			blockData: {
				label: json.blockData.label,
				actionType: json.blockData.actionType,
				url: json.blockData.url,
				target: json.blockData.target,
				color: cssStyles?.['color'],
				backgroundColor: cssStyles?.['background-color'],
				hoverColor: contentManager.getStylesForSelector(json.selector + ":hover")?.['color'],
				hoverBackgroundColor: contentManager.getStylesForSelector(json.selector + ":hover")?.['background-color'],
				fontFamily: cssStyles?.['font-family'],
				fontSize: blocksManager.parseDimensions(cssStyles?.['font-size'], "single"),
				fontWeight: cssStyles?.['font-weight'],
				lineHeight: blocksManager.parseDimensions(cssStyles?.['line-height'], "single"),
				letterSpacing: blocksManager.parseDimensions(cssStyles?.['letter-spacing'], "single"),
				textDecoration: cssStyles?.['text-decoration'],
				padding: blocksManager.parseDimensions(cssStyles?.['padding'], "four-side"),
				margin: blocksManager.parseDimensions(cssStyles?.['margin'], "four-side"),
				borderRadius: blocksManager.parseDimensions(cssStyles?.['border-radius'], "four-corners"),
				borderStyle: cssStyles?.['border-style'],
				borderWidth: blocksManager.parseDimensions(cssStyles?.['border-width'], "single"),
				borderColor: cssStyles?.['border-color'],
				width: blocksManager.parseDimensions(cssStyles?.['width'], "single"),
				alignment: cssStyles?.['text-align'],
			},
		} as IContentBlock,
	};

	blocksManager.addDesignTreeEntry({
		contentBlockId: block.contentBlock.contentBlockId,
		designBlockId: block.designBlockId,
		designBlockRef: newDesignBlockRef,
		contentBlockRef: newContentBlockRef,
		contentBlockType: block.contentBlock.blockType.toUpperCase(),
		location: context,
	});

	return block;
}